import React, { useEffect, useState } from "react";
import { MButton } from "@/components/ui/atoms/MButtons";
import { MInputField } from "@/components/ui/atoms/MInputField";
import { MToggle } from "@/components/ui/atoms/MToggle";
import { useVitals } from "@/hooks/useVitals";
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import {
  sterilizationOptions,
  SterilizationStatus,
  UnitType,
  VitalName,
} from "@/types/vitals";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconCaretDown } from "@tabler/icons-react";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { useNavigationSource } from "@/hooks/useNavigationSource";

const Vitals: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const isPet = memberInfo.memberType === "p";

  // Toast
  const { showToast } = useToast();

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();

  // Vitals hook manages form state
  const {
    isLoading,
    isError,
    createVitals,
    isCreateError,
    isCreateSuccess,
    isChanged,
    heightFt,
    setHeightFt,
    heightIn,
    setHeightIn,
    heightCm,
    setHeightCm,
    heightUnit,
    handleUnitToggle,
    weight,
    setWeight,
    sterilizationStatus,
    setSterilizationStatus,
  } = useVitals(memberId);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.VITALS,
    });
  }, [memberInfo?.id]);

  // Toast for error/success states
  useEffect(() => {
    if (isCreateError) {
      showToast({ message: t("errors.toast"), variant: "error" });
    }
    if (isError) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    }
  }, [isCreateSuccess, isCreateError, isError]);

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberId) return;
    const vitalsData = [];
    if (isPet) {
      // 1. Update weight vital
      vitalsData.push({
        name: VitalName.WEIGHT,
        value: weight,
        unit: UnitType.KG,
      });
      vitalsData.push({
        name: VitalName.STERILIZATION,
        value: sterilizationStatus,
      });
    } else {
      // Submit height based on selected unit
      if (heightUnit === "cm") {
        vitalsData.push({
          name: VitalName.HEIGHT,
          value: heightCm,
          unit: UnitType.CM,
        });
      } else {
        const heightValue = heightFt ? `${heightFt}'${heightIn}` : "";
        vitalsData.push({
          name: VitalName.HEIGHT,
          value: heightValue,
          unit: UnitType.FT_IN,
        });
      }
      vitalsData.push({
        name: VitalName.WEIGHT,
        value: weight,
        unit: UnitType.KG,
      });
    }

    try {
      await createVitals({
        vitals: vitalsData,
      });
    } catch {
      // Error toast is already shown via the isCreateError effect above —
      // stay on this page instead of navigating away as if the save
      // succeeded, so the page we return to never races a stale read
      // against this write.
      return;
    }

    // Track completion event
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_VITALS_COMPLETE,
    });

    // Navigate back to source if exists, otherwise go to basic information.
    // createVitals is awaited above (and itself refetches internally), so by
    // the time we navigate the write is guaranteed committed — the page we
    // land on will no longer race its own GET against this save.
    const didNavigate = navigateBackIfExists();
    if (!didNavigate) {
      navigate("/profile/basic-information");
    }
  };

  const isValidData = (): boolean => {
    if (isPet) {
      return (
        weight !== "" && sterilizationStatus !== "" && Number(weight) <= 100
      );
    } else {
      if (heightUnit === "cm") {
        return (
          heightCm !== "" &&
          weight !== "" &&
          Number(heightCm) > 0 &&
          Number(heightCm) <= 300
        );
      } else {
        return (
          heightFt !== "" &&
          heightIn !== "" &&
          weight !== "" &&
          Number(heightFt) <= 20 &&
          Number(heightIn) < 12
        );
      }
    }
  };

  const getHeightLabel = () => {
    return memberInfo.onBehalf
      ? t("vitals.whatsTheirHeight")
      : t("vitals.whatsYourHeight");
  };

  const getWeightLabel = () => {
    return memberInfo.onBehalf
      ? t("vitals.whatsTheirWeight")
      : t("vitals.whatsYourWeight");
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader module="Vitals" memberInfo={memberInfo} />

      <MSpacer />
      <form className="flex flex-col flex-1" onSubmit={handleSubmit}>
        {!isPet && (
          <div className="mb-[var(--scale-padding-4xl)]">
            <div className="flex items-center justify-between mb-[var(--scale-padding-lg)]">
              <label className="profile-input-label vitals-label">
                {getHeightLabel()}
              </label>
              <MToggle
                variant="labeled"
                leftLabel="cm"
                rightLabel="in"
                checked={heightUnit === "ft/in"}
                onChange={(checked) => handleUnitToggle(!checked)}
                ariaLabel="Height unit toggle"
              />
            </div>
            {heightUnit === "cm" ? (
              <MInputField
                value={heightCm}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setHeightCm(value);
                }}
                placeholder="Not Answered"
                variant="Underlined"
                size="md"
                type="number"
                inputMode="numeric"
                showLabel={false}
                showSIUnit={true}
                siUnit="cm"
                style={{ minWidth: 0, width: "156.5px" }}
              />
            ) : (
              <div className="flex items-end gap-8 self-stretch">
                <MInputField
                  value={heightFt}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setHeightFt(value);
                  }}
                  placeholder="Not Answered"
                  variant="Underlined"
                  size="md"
                  type="number"
                  inputMode="numeric"
                  showLabel={false}
                  showSIUnit={true}
                  siUnit={t("vitals.ft")}
                  style={{ minWidth: 0, width: "156.5px" }}
                />
                <MInputField
                  value={heightIn}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setHeightIn(value);
                  }}
                  placeholder="Not Answered"
                  variant="Underlined"
                  size="md"
                  type="number"
                  inputMode="numeric"
                  showLabel={false}
                  showSIUnit={true}
                  siUnit={t("vitals.in")}
                  style={{ minWidth: 0, width: "156.5px" }}
                />
              </div>
            )}
          </div>
        )}
        <div>
          <label className="profile-input-label vitals-label">
            {getWeightLabel()}
          </label>
          <MInputField
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            type="number"
            showSIUnit={true}
            siUnit={t("vitals.kg")}
            style={{ minWidth: 0, width: "156.5px" }}
          />
        </div>
        {isPet && (
          <div className="mt-[var(--scale-padding-4xl)]">
            <label className="profile-input-label vitals-label">
              {t("vitals.sterelization")}
            </label>
            <MInputField
              value={
                sterilizationOptions.find(
                  (opt) => opt.value === sterilizationStatus
                )?.label
              }
              onChange={(e) => setWeight(e.target.value)}
              placeholder={t("common.notAnswered")}
              variant="Underlined"
              size="md"
              onClick={() => setDrawerOpen(true)}
              icon={<IconCaretDown className="vital-icon" />}
              showIcon={true}
            />
            <MDropDownDrawer
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              title="Select Sterilization Status"
              items={sterilizationOptions}
              initialValue={sterilizationStatus}
              onSelect={(val) => {
                setSterilizationStatus(val as SterilizationStatus);
                setDrawerOpen(false);
              }}
              variant="single"
            />
          </div>
        )}
        <div className="confirm-footer">
          <MButton
            className="w-full"
            variant="filled"
            size="lg"
            type="submit"
            text={t("common.done")}
            disabled={!isChanged() || isLoading || !isValidData()}
          />
        </div>
      </form>
    </div>
  );
};

export default Vitals;
